<template>
  <div
    class="card-slot"
    :class="[
      `slot-${position}`,
      { 'is-active': isActive, 'is-valid': isValid, 'is-invalid': isInvalid, 'is-complete': isComplete, 'is-mobile': isMobile }
    ]"
    @click="$emit('click', position)"
    @dragover.prevent
    @drop="onDrop"
  >
    <div v-if="showHeader" class="slot-header">
      <span class="slot-name">{{ slotName }}</span>
      <span class="slot-count">{{ cards.length }}/{{ maxCards }}</span>
    </div>
    <div class="slot-cards">
      <div 
        v-for="(card, index) in displayCards" 
        :key="card.id || index"
        class="slot-card-wrapper"
        :style="getCardStyle(index)"
      >
        <PlayingCard
          :card="card"
          :face-down="faceDown"
          :size="isMobile ? 'small' : 'normal'"
          @click.stop="$emit('card-click', { card, position })"
          @dblclick="onCardDblClick(card, $event)"
        />
      </div>
      <div v-if="cards.length === 0" class="slot-empty">
        <span class="empty-text">{{ emptyText }}</span>
      </div>
    </div>
    <div v-if="showHeader && handType" class="hand-type">
      {{ handType }}
    </div>
  </div>
</template>

<script>
import PlayingCard from './PlayingCard.vue'

export default {
  name: 'CardSlot',
  components: { PlayingCard },
  data() {
    return {
      isMobile: false
    }
  },
  mounted() {
    this.isMobile = window.innerWidth <= 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  },
  props: {
    position: {
      type: String,
      required: true, // 'front', 'middle', 'back'
      validator: value => ['front', 'middle', 'back'].includes(value)
    },
    cards: {
      type: Array,
      default: () => []
    },
    isActive: {
      type: Boolean,
      default: false
    },
    isValid: {
      type: Boolean,
      default: true
    },
    isInvalid: {
      type: Boolean,
      default: false
    },
    handType: {
      type: String,
      default: ''
    },
    faceDown: {
      type: Boolean,
      default: false
    },
    showHeader: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    slotName() {
      const names = {
        front: '头道 (3张)',
        middle: '中道 (5张)',
        back: '尾道 (5张)'
      }
      return names[this.position]
    },
    maxCards() {
      return this.position === 'front' ? 3 : 5
    },
    isComplete() {
      return this.cards.length === this.maxCards
    },
    displayCards() {
      return this.cards
    },
    emptyText() {
      const texts = {
        front: '放入3张牌',
        middle: '放入5张牌',
        back: '放入5张牌'
      }
      return texts[this.position]
    }
  },
  methods: {
    getCardStyle(index) {
      // 使用负margin实现牌的重叠效果
      // 第一张牌不偏移，后续每张牌向左偏移
      if (index === 0) {
        return { zIndex: index }
      }
      const overlap = window.innerWidth <= 768 ? '-16px' : '-29px'
      return {
        marginLeft: overlap,
        zIndex: index
      }
    },
    onDrop(event) {
      const cardData = event.dataTransfer.getData('card')
      if (cardData) {
        const card = JSON.parse(cardData)
        this.$emit('drop', { card, position: this.position })
      }
    },
    onCardDblClick(card, event) {
      // 阻止事件冒泡
      if (event && event.stopPropagation) {
        event.stopPropagation()
      }
      this.$emit('card-dblclick', { card, position: this.position })
    }
  }
}
</script>

<style lang="scss" scoped>
.card-slot {
  background: rgba(0, 0, 0, 0.3);
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  padding: 12px;
  height: 180px; // 固定高度
  transition: all 0.3s ease;
  cursor: pointer;
  // 固定宽度：头道3张牌宽度，中道/尾道5张牌宽度
  // 牌宽68px，重叠29px，内边距24px
  &.slot-front {
    width: 170px; // 3张牌: 68 + 2*39 = 146, 加上内边距
  }
  &.slot-middle,
  &.slot-back {
    width: 248px; // 5张牌: 68 + 4*39 = 224, 加上内边距
  }

  &:hover {
    background: rgba(0, 0, 0, 0.4);
    border-color: rgba(255, 255, 255, 0.5);
  }

  &.is-active {
    background: rgba(255, 215, 0, 0.15);
    border-color: #ffd700;
    box-shadow: 0 0 15px rgba(255, 215, 0, 0.3);
  }

  &.is-valid {
    border-color: #27ae60;
    background: rgba(39, 174, 96, 0.1);
  }

  &.is-invalid {
    border-color: #e74c3c;
    background: rgba(231, 76, 60, 0.1);
  }

  &.is-complete {
    border-style: solid;
  }
}

.slot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
}

.slot-name {
  font-weight: bold;
}

.slot-count {
  opacity: 0.7;
}

.slot-cards {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 101px;
  padding: 0 10px; // 左右内边距
}

.slot-card-wrapper {
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    z-index: 100 !important;
    transform: translateY(-5px);
  }
}

.slot-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 94px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
}

.hand-type {
  text-align: center;
  margin-top: 8px;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: #ffd700;
  font-size: 12px;
  font-weight: bold;
}

// 移动端样式（通过 JS 类名触发）
.card-slot.is-mobile {
  padding: 6px;
  height: 88px;
  min-height: auto;

  &.slot-front {
    width: 102px;
  }

  &.slot-middle,
  &.slot-back {
    width: 148px;
  }

  .slot-header {
    font-size: 10px;
    margin-bottom: 2px;
  }

  .slot-cards {
    min-height: auto;
    padding: 0 3px;
  }

  .slot-empty {
    height: 64px;
    font-size: 10px;
  }

  .hand-type {
    font-size: 9px;
    margin-top: 2px;
    padding: 2px 4px;
  }
}
</style>
